// src/pages/NewCasePage.js
import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import API_BASE from '../apiConfig';
import './NewCasePage.css';
import BackToDashboard from "../components/BackToDashboard";
import proj4 from 'proj4';
import kommunerRaw from '../data/kommuner';
import AuthContext from '../AuthContext';

// ========== KONSTANTER ==========
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY';
const EPSG3006 = "+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs";
const WGS84 = "+proj=longlat +datum=WGS84 +no_defs";
const DEFAULT_TRANSPORT = 'Vägtransport';

// Kommunlista med robust fallback
const ALLA_KOMMUNER = Array.isArray(kommunerRaw)
  ? kommunerRaw
  : (Array.isArray(kommunerRaw?.default)
    ? kommunerRaw.default
    : (Array.isArray(kommunerRaw?.ALLA_KOMMUNER)
      ? kommunerRaw.ALLA_KOMMUNER
      : []));

const SAFE_KOMMUNER = (ALLA_KOMMUNER && ALLA_KOMMUNER.length > 0)
  ? ALLA_KOMMUNER
  : ["Göteborg", "Götene", "Stockholm", "Gävle", "Malmö", "Uppsala"];

// ===== Helper: ladda Google Maps-script dynamiskt (en gång) =====
let mapsScriptLoading = null;
function loadGoogleMapsScript(apiKey) {
  if (typeof window !== 'undefined' && window.google && window.google.maps) return Promise.resolve();
  if (mapsScriptLoading) return mapsScriptLoading;

  mapsScriptLoading = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') return resolve();

    const existing = document.querySelector('script[data-gmaps="yes"]');
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }

    const s = document.createElement('script');
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    s.dataset.gmaps = 'yes';
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });

  return mapsScriptLoading;
}

// ===== Helpers för adressparsing =====
function getComponent(components, type) {
  const comp = components?.find(c => c.types.includes(type));
  return comp ? comp.long_name : '';
}

function parseAddressComponents(components) {
  if (!components) return {};

  const postnummer = getComponent(components, 'postal_code');
  const kommun =
    getComponent(components, 'administrative_area_level_2') ||
    getComponent(components, 'postal_town') ||
    getComponent(components, 'locality') ||
    '';

  const street = getComponent(components, 'route');
  const streetNumber = getComponent(components, 'street_number');
  const ort =
    getComponent(components, 'locality') ||
    getComponent(components, 'postal_town') ||
    '';

  const gatuadress = [street, streetNumber].filter(Boolean).join(' ');
  const postadress = [postnummer, ort].filter(Boolean).join(' ');

  return {
    postnummer: postnummer || '',
    kommun: kommun || '',
    gatuadress: gatuadress || '',
    postadress: postadress || '',
  };
}

// ===== EWC helpers =====
function getEwcDigits(code) {
  return String(code || '').replace(/\D/g, '');
}

function normalizeEwcDisplay(code) {
  const digits = getEwcDigits(code);
  if (digits.length !== 6) return code || '';
  return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)}`;
}

function buildFullEwcCode(item) {
  if (!item) return '';
  const rawCode = String(item.code || '');
  const rawDesc = String(item.description || '');
  const baseDigits = getEwcDigits(rawCode);

  let lastTwo = '';
  const m = rawDesc.match(/^(\d{2})\s*\*/);
  if (m) lastTwo = m[1];

  const combined = baseDigits + lastTwo;

  if (combined.length === 6) {
    return `${combined.slice(0, 2)} ${combined.slice(2, 4)} ${combined.slice(4, 6)}`;
  }

  if (baseDigits.length === 6) {
    return normalizeEwcDisplay(baseDigits);
  }

  return normalizeEwcDisplay(rawCode);
}

function isHazardousWasteItem(item) {
  if (!item) return false;

  const displayCode =
    item.displayCode ||
    buildFullEwcCode(item) ||
    String(item.code || '');

  const desc = String(item.description || '');

  return (
    /\*/.test(displayCode) ||
    /\*/.test(String(item.code || '')) ||
    /farligt avfall/i.test(desc)
  );
}

function codeKeyFromItem(item) {
  if (!item) return '';
  const full = buildFullEwcCode(item);
  const digits = getEwcDigits(full);
  return digits || full.replace(/\s+/g, '').toLowerCase();
}

function rebuildDeliveryPlaceSummary(next) {
  const typ = next.deliveryPlaceTyp === 'sweref' ? 'sweref' : 'adress';

  if (typ === 'sweref' && next.deliveryPlaceX && next.deliveryPlaceY) {
    const nm = next.deliveryPlaceName || 'Plats';
    return `${nm} – SWEREF: ${next.deliveryPlaceX}, ${next.deliveryPlaceY}`.trim();
  }

  const parts = [];
  const nm = next.deliveryPlaceName || '';
  if (nm) parts.push(nm);

  const addrParts = [];
  if (next.deliveryPlaceGatuadress) addrParts.push(next.deliveryPlaceGatuadress);
  if (next.deliveryPlaceAdress) addrParts.push(next.deliveryPlaceAdress);

  if (addrParts.length) parts.push(`– ${addrParts.join(', ')}`);

  const pk = `${next.deliveryPlacePostnummer || ''} ${next.deliveryPlaceKommun || ''}`.trim();
  if (pk) parts.push(`, ${pk}`);

  return parts.join(' ').replace(/\s+,/g, ',').trim();
}

// ===== MapPicker =====
function MapPicker({ onChangeXY, onChangeLatLng, onAddressResolved }) {
  const mapRef = useRef(null);
  const searchRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [lat, setLat] = useState(57.70887);
  const [lng, setLng] = useState(11.97456);

  const updateXY = (latNew, lngNew) => {
    const [x, y] = proj4(WGS84, EPSG3006, [lngNew, latNew]);
    onChangeXY?.({ x: x.toFixed(2), y: y.toFixed(2) });
    onChangeLatLng?.({ lat: latNew, lng: lngNew });
  };

  const geocodeAndFill = (g, latNew, lngNew) => {
    try {
      if (!geocoderRef.current) geocoderRef.current = new g.maps.Geocoder();
      geocoderRef.current.geocode({ location: { lat: latNew, lng: lngNew } }, (results, status) => {
        if (status === 'OK' && Array.isArray(results) && results.length > 0) {
          const exact = results.find(r =>
            r.types?.includes('street_address') ||
            r.types?.includes('premise') ||
            r.types?.includes('route')
          ) || results[0];

          const parsed = parseAddressComponents(exact.address_components || []);
          onAddressResolved?.(parsed);
        }
      });
    } catch {}
  };

  useEffect(() => {
    let mounted = true;
    let map;
    let autocomplete;

    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY).then(() => {
      if (!mounted || !mapRef.current || typeof window === 'undefined' || !window.google) return;

      const g = window.google;

      map = new g.maps.Map(mapRef.current, {
        center: { lat, lng },
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
      });

      markerRef.current = new g.maps.Marker({
        position: { lat, lng },
        map,
        draggable: true,
      });

      g.maps.event.addListener(markerRef.current, 'dragend', () => {
        const p = markerRef.current.getPosition();
        if (!p) return;

        const newLat = p.lat();
        const newLng = p.lng();
        setLat(newLat);
        setLng(newLng);
        updateXY(newLat, newLng);
        geocodeAndFill(g, newLat, newLng);
      });

      if (searchRef.current && g.maps.places?.Autocomplete) {
        autocomplete = new g.maps.places.Autocomplete(searchRef.current, {
          fields: ['geometry', 'name', 'formatted_address', 'address_components', 'place_id'],
          types: ['geocode'],
          componentRestrictions: { country: 'se' },
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          const loc = place?.geometry?.location;
          if (!loc) return;

          map.panTo(loc);
          markerRef.current.setPosition(loc);

          const newLat = loc.lat();
          const newLng = loc.lng();
          setLat(newLat);
          setLng(newLng);
          updateXY(newLat, newLng);

          if (Array.isArray(place.address_components) && place.address_components.length > 0) {
            const parsed = parseAddressComponents(place.address_components);
            onAddressResolved?.(parsed);
          } else {
            geocodeAndFill(g, newLat, newLng);
          }
        });
      }

      updateXY(lat, lng);
      geocodeAndFill(g, lat, lng);
    });

    return () => {
      mounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openInGoogleMaps = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  return (
    <div className="mappicker-wrap" style={{ display: 'grid', gap: 8 }}>
      <input
        ref={searchRef}
        placeholder="Sök kommun/område…"
        className="map-input"
        style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}
      />
      <div
        ref={mapRef}
        style={{ height: 320, borderRadius: 8, border: '1px solid #e2e8f0' }}
      />
      <button type="button" className="btn-secondary" onClick={openInGoogleMaps}>
        Öppna i Google Maps
      </button>
    </div>
  );
}

// ===================== HUVUDKOMPONENT =====================
function NewCasePage() {
  const { currentCompany, user: ctxUser } = useContext(AuthContext);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const effectiveCompanyId = useMemo(() => {
    const cFromUser = toNum(ctxUser?.company_id);
    if (cFromUser) return cFromUser;

    const cFromCtx = toNum(currentCompany?.id);
    if (cFromCtx) return cFromCtx;

    try {
      const raw = localStorage.getItem('currentCompany');
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return toNum(obj?.id) || null;
    } catch {
      return null;
    }
  }, [ctxUser?.company_id, currentCompany?.id]);

  const userIdFromCtxOrLS = useMemo(() => {
    if (ctxUser?.id) return Number(ctxUser.id);

    const keys = ['userId', 'authUser', 'user', 'currentUser'];
    for (const k of keys) {
      const raw = localStorage.getItem(k);
      if (!raw) continue;

      if (k === 'userId') {
        const n = Number(raw);
        if (!Number.isNaN(n) && n > 0) return n;
        continue;
      }

      try {
        const obj = JSON.parse(raw);
        if (obj?.id) return Number(obj.id);
        if (obj?.user?.id) return Number(obj.user.id);
      } catch {}
    }

    return null;
  }, [ctxUser?.id]);

  const userEmail = useMemo(() => {
    const norm = (v) => (v ? String(v).trim().toLowerCase() : null);

    if (norm(ctxUser?.email)) return norm(ctxUser.email);
    if (norm(currentCompany?.email)) return norm(currentCompany.email);

    const keys = ['authUser', 'user', 'currentUser', 'company', 'auth', 'session'];
    for (const k of keys) {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        const obj = JSON.parse(raw);
        if (norm(obj?.email)) return norm(obj.email);
        if (norm(obj?.user?.email)) return norm(obj.user.email);
        if (norm(obj?.company?.email)) return norm(obj.company.email);
      } catch {}
    }

    if (norm(localStorage.getItem('email'))) return norm(localStorage.getItem('email'));
    if (norm(localStorage.getItem('userEmail'))) return norm(localStorage.getItem('userEmail'));

    return null;
  }, [ctxUser?.email, currentCompany?.email]);

  const [derivedUserId, setDerivedUserId] = useState(null);

  useEffect(() => {
    let gone = false;

    async function resolveUserId() {
      try {
        if (userIdFromCtxOrLS || !effectiveCompanyId || !userEmail) return;

        const res = await fetch(`${API_BASE}/users?companyId=${effectiveCompanyId}`, {
          credentials: 'include',
        });

        const data = await res.json();
        if (!res.ok || !Array.isArray(data)) return;

        const match = data.find(u => String(u.email || '').toLowerCase() === userEmail);
        if (match && !gone) setDerivedUserId(Number(match.id));
      } catch (e) {
        console.warn('[NewCase] resolveUserId fail:', e?.message || e);
      }
    }

    resolveUserId();
    return () => { gone = true; };
  }, [userIdFromCtxOrLS, userEmail, effectiveCompanyId]);

  const userId = userIdFromCtxOrLS ?? derivedUserId ?? null;

  const nsCompany = effectiveCompanyId ?? currentCompany?.id ?? 'anonCompany';
  const nsUser = userId ?? userEmail ?? 'anonUser';

  const logoKey = useMemo(() => `companyLogo:${nsCompany}`, [nsCompany]);
  const placesKey = useMemo(() => `mottagningsplatser:${nsCompany}:${nsUser}`, [nsCompany, nsUser]);
  const pendingKey = useMemo(() => `pendingTransport:${nsCompany}:${nsUser}`, [nsCompany, nsUser]);
  const hiddenCodesKey = useMemo(() => `wastecodes:hidden:${nsCompany}:${nsUser}`, [nsCompany, nsUser]);

  const identityReady = Boolean(effectiveCompanyId) && Boolean(userId || userEmail);

  const [companySettings, setCompanySettings] = useState(null);
  const [companySettingsStatus, setCompanySettingsStatus] = useState('idle');
  const [userSettings, setUserSettings] = useState(null);

  const [logo, setLogo] = useState('');
  const [platser, setPlatser] = useState([]);
  const [platserLamnades, setPlatserLamnades] = useState([]);
  const [selectedDeliveredId, setSelectedDeliveredId] = useState('');

  const [savedCodes, setSavedCodes] = useState([]);
  const [hiddenCodeKeys, setHiddenCodeKeys] = useState([]);
  const [selectedCodeIdx, setSelectedCodeIdx] = useState(null);
  const [lastJustSelected, setLastJustSelected] = useState('');
  const [prevHolderInput, setPrevHolderInput] = useState('');
  const [showKommuner, setShowKommuner] = useState(false);

  const [caseData, setCaseData] = useState({
    name: '',
    wasteCode: '',
    wasteTypes: '',
    amount: '',
    unit: 'kg',
    pickupAddress: '',
    pickupDate: '',
    contactPerson: '',
    contactPhone: '',
    notes: '',

    personnummer: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',

    orgNumber: '',
    companyName: '',
    cfar: '',
    transportsatt: DEFAULT_TRANSPORT,

    platsNamn: '',
    platsGatuadress: '',
    platsAdress: '',
    platsPostnummer: '',
    platsKommun: '',
    platsX: '',
    platsY: '',

    deliveryPlace: '',
    deliveryPlaceName: '',
    deliveryPlaceGatuadress: '',
    deliveryPlaceAdress: '',
    deliveryPlacePostnummer: '',
    deliveryPlaceKommun: '',
    deliveryPlaceX: '',
    deliveryPlaceY: '',
    deliveryPlaceTyp: 'adress',
    receiverEmail: '',

    previousHolders: [],
  });

  // ========= Logo =========
  useEffect(() => {
    const stored = localStorage.getItem(logoKey) || localStorage.getItem('companyLogo') || '';
    setLogo(stored || '');
  }, [logoKey]);

  // ======== SERVERDATA: company-settings ========
  useEffect(() => {
    if (!identityReady || !effectiveCompanyId) return;
    const ctl = new AbortController();

    (async () => {
      try {
        setCompanySettingsStatus('loading');

        const r = await fetch(
          `${API_BASE}/company-settings?companyId=${effectiveCompanyId}`,
          {
            signal: ctl.signal,
            credentials: 'include',
          }
        );

        const json = await r.json();
        if (!r.ok) throw new Error(json?.error || 'Kunde inte hämta företagsinställningar');

        const prevList = Array.isArray(json.previous_holders_list)
          ? json.previous_holders_list
          : String(json.previous_holders || '')
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);

        const deliveredPlaces = Array.isArray(json.saved_places_delivered)
          ? json.saved_places_delivered
          : [];

        setPlatserLamnades(deliveredPlaces);

        setCompanySettings({
          ...json,
          previous_holders_list: prevList,
        });

        setCompanySettingsStatus('ready');

        if (json.logo_url || json.logo) {
          const url = json.logo_url || json.logo;
          setLogo(url);
          try {
            localStorage.setItem(logoKey, url);
          } catch {}
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[NewCase] company-settings:', e.message);
          setCompanySettings({});
          setCompanySettingsStatus('error');
        }
      }
    })();

    return () => ctl.abort();
  }, [identityReady, effectiveCompanyId, logoKey]);

  // ======== SERVERDATA: user-settings ========
  useEffect(() => {
    if (!identityReady || !userId || !effectiveCompanyId) return;
    const ctl = new AbortController();

    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/user-settings?companyId=${effectiveCompanyId}&user_id=${userId}`,
          {
            signal: ctl.signal,
            credentials: 'include',
          }
        );

        const json = await r.json();
        if (!r.ok) {
          throw new Error(json?.error || 'Kunde inte hämta user-settings');
        }

        setUserSettings(json?.data || null);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[NewCase] user-settings:', e.message);
        }
      }
    })();

    return () => ctl.abort();
  }, [identityReady, userId, effectiveCompanyId]);

  // ======== Sparade koder ========
  useEffect(() => {
    const list = Array.isArray(companySettings?.saved_waste_codes)
      ? companySettings.saved_waste_codes
      : [];
    setSavedCodes(list);
  }, [companySettings]);

  useEffect(() => {
    if (!identityReady) return;

    const baseList = Array.isArray(companySettings?.saved_waste_codes)
      ? companySettings.saved_waste_codes
      : [];

    let hidden = [];
    try {
      const raw = localStorage.getItem(hiddenCodesKey);
      if (raw) hidden = JSON.parse(raw);
    } catch {
      hidden = [];
    }

    const normalizedHidden = Array.isArray(hidden) ? hidden : [];
    setHiddenCodeKeys(normalizedHidden);

    const hiddenSet = new Set(normalizedHidden);
    const filtered = baseList.filter((item) => {
      const key = codeKeyFromItem(item);
      return key && !hiddenSet.has(key);
    });

    setSavedCodes(filtered);
  }, [identityReady, companySettings, hiddenCodesKey]);

  // ======== Platser senast hanterades ========
  useEffect(() => {
    if (!identityReady) return;

    const dbPlaces = Array.isArray(userSettings?.saved_places) ? userSettings.saved_places : null;

    if (dbPlaces) {
      setPlatser(dbPlaces);
      try {
        localStorage.setItem(placesKey, JSON.stringify(dbPlaces));
      } catch {}
      return;
    }

    try {
      const savedPlatser = localStorage.getItem(placesKey) || localStorage.getItem('mottagningsplatser');
      if (savedPlatser) {
        const parsed = JSON.parse(savedPlatser);
        if (Array.isArray(parsed)) {
          setPlatser(parsed);
        }
      }
    } catch {}
  }, [identityReady, userSettings, placesKey]);

  // ======== Restore pending ========
  useEffect(() => {
    try {
      const saved = localStorage.getItem(pendingKey);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object') {
        setCaseData(prev => ({
          ...prev,
          ...parsed,
        }));
      }
    } catch (e) {
      console.warn('[NewCase] kunde inte läsa pendingTransport:', e);
    }
  }, [pendingKey]);

  // ======== Prefill ========
  useEffect(() => {
    if (!identityReady) return;

    setCaseData(prev => {
      const u = userSettings || {};
      const c = companySettings || {};

      const ctxCompanyName =
        c?.verksamhetsnamn ||
        currentCompany?.company_name ||
        currentCompany?.companyName ||
        '';

      return {
        ...prev,
        personnummer: u?.personnummer || '',
        firstName: u?.fornamn || '',
        lastName: u?.efternamn || '',
        email: u?.uppgiftsemail || ctxUser?.email || '',
        phone: u?.phone || '',

        orgNumber: c?.org_number || currentCompany?.org_number || currentCompany?.orgNumber || '',
        companyName: ctxCompanyName,
        contactPerson: c?.contact_person || '',
        contactPhone: c?.kontaktTelefon || '',
        cfar: c?.cfar || u?.cfar || '',
        transportsatt: u?.transportsatt || c?.transportsatt || DEFAULT_TRANSPORT,

        pickupAddress: prev.pickupAddress || c?.address || '',
        deliveryPlace: prev.deliveryPlace || c?.delivery_place_default || '',
        deliveryPlaceName: prev.deliveryPlaceName || '',
        receiverEmail: prev.receiverEmail || c?.receiver_email || '',

        previousHolders:
          Array.isArray(prev.previousHolders) && prev.previousHolders.length > 0
            ? prev.previousHolders
            : (c?.previous_holders_list || []),
      };
    });
  }, [identityReady, userSettings, companySettings, currentCompany, ctxUser?.email]);

  async function syncSavedCodesToServer(nextList) {
    try {
      const companyId = effectiveCompanyId || currentCompany?.id;
      if (!companyId) return;

      const payloadList = nextList.map((it) => ({
        code: it.code,
        description: it.description,
      }));

      const res = await fetch(`${API_BASE}/company-settings/waste-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          companyId,
          saved_waste_codes: payloadList,
        }),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok === false) {
        console.warn('[NewCase] kunde inte spara waste-codes till servern:', json?.error || res.status);
      }
    } catch (e) {
      console.warn('[NewCase] nätverksfel vid sparning av waste-codes:', e);
    }
  }

  function handleSelectCode(item, idx) {
    const fullCode = buildFullEwcCode(item);

    setCaseData(prev => ({ ...prev, wasteCode: fullCode }));

    const digits = getEwcDigits(fullCode);
    const existingIdx = savedCodes.findIndex(
      (c) => getEwcDigits(buildFullEwcCode(c)) === digits
    );

    let nextList = savedCodes;
    let finalIdx = existingIdx;

    if (existingIdx === -1) {
      const newItem = {
        code: item.code || fullCode,
        description: item.description || '',
      };
      nextList = [...savedCodes, newItem];
      finalIdx = nextList.length - 1;
      syncSavedCodesToServer(nextList);
    }

    setSelectedCodeIdx(finalIdx);
    setLastJustSelected(fullCode);
    window.setTimeout(() => setLastJustSelected(''), 400);
  }

  function handleRemoveCode(codeToRemove, idxToRemove = null) {
    const item = idxToRemove != null ? savedCodes[idxToRemove] : null;
    const key = item ? codeKeyFromItem(item) : codeKeyFromItem({ code: codeToRemove });

    if (!key) return;

    const nextHidden = Array.from(new Set([...hiddenCodeKeys, key]));
    setHiddenCodeKeys(nextHidden);
    try {
      localStorage.setItem(hiddenCodesKey, JSON.stringify(nextHidden));
    } catch {}

    const filtered = savedCodes.filter((it) => codeKeyFromItem(it) !== key);
    setSavedCodes(filtered);

    const removedDigits = getEwcDigits(codeToRemove);
    const currentDigits = getEwcDigits(caseData.wasteCode);
    if (
      selectedCodeIdx === idxToRemove ||
      (removedDigits && removedDigits === currentDigits)
    ) {
      setSelectedCodeIdx(null);
      setCaseData(prev => ({ ...prev, wasteCode: '' }));
    }
  }

  async function handleSaveCurrentCode() {
    const digits = getEwcDigits(caseData.wasteCode);

    if (!digits || digits.length !== 6) {
      alert('Välj en giltig avfallskod (6 siffror) först.');
      return;
    }

    const baseList = Array.isArray(companySettings?.saved_waste_codes)
      ? companySettings.saved_waste_codes
      : [];

    const already = baseList.some(
      (it) => getEwcDigits(buildFullEwcCode(it)) === digits
    );

    if (already) {
      alert('Denna avfallskod är redan sparad för företaget.');
      return;
    }

    const newItem = {
      code: caseData.wasteCode,
      description: caseData.wasteTypes || '',
    };

    const newCompanyList = [...baseList, newItem];
    await syncSavedCodesToServer(newCompanyList);

    setCompanySettings(prev => ({
      ...(prev || {}),
      saved_waste_codes: newCompanyList,
    }));

    const newKey = codeKeyFromItem(newItem);
    const nextHidden = hiddenCodeKeys.filter(k => k !== newKey);
    setHiddenCodeKeys(nextHidden);
    try {
      localStorage.setItem(hiddenCodesKey, JSON.stringify(nextHidden));
    } catch {}

    setSavedCodes(prev => [...prev, newItem]);
    setSelectedCodeIdx(newCompanyList.length - 1);
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setCaseData(prev => {
      const next = { ...prev, [name]: value };

      const deliveryFields = new Set([
        'deliveryPlaceName',
        'deliveryPlaceGatuadress',
        'deliveryPlaceAdress',
        'deliveryPlacePostnummer',
        'deliveryPlaceKommun',
        'deliveryPlaceX',
        'deliveryPlaceY',
        'deliveryPlaceTyp',
      ]);

      if (deliveryFields.has(name)) {
        next.deliveryPlace = rebuildDeliveryPlaceSummary(next);
      }

      if (name === 'deliveryPlace') {
        next.deliveryPlace = value;
      }

      return next;
    });
  }

  function handlePlatsSelect(e) {
    const platsId = Number(e.target.value);
    const plats = platser.find(p => p.id === platsId);

    if (plats) {
      setCaseData(prev => ({
        ...prev,
        platsNamn: plats.namn || '',
        platsGatuadress: plats.gatuadress || '',
        platsAdress: plats.adress || '',
        platsPostnummer: plats.postnummer || '',
        platsKommun: plats.kommun || '',
        platsX: plats.x || '',
        platsY: plats.y || '',
        pickupAddress: plats.adress
          ? plats.adress
          : (plats.x && plats.y ? `SWEREF: ${plats.x}, ${plats.y}` : (companySettings?.address || '')),
      }));
    } else {
      setCaseData(prev => ({
        ...prev,
        platsNamn: '',
        platsGatuadress: '',
        platsAdress: '',
        platsPostnummer: '',
        platsKommun: '',
        platsX: '',
        platsY: '',
        pickupAddress: companySettings?.address || '',
      }));
    }
  }

  const handleDeliveredPlaceSelect = (e) => {
    const idStr = e.target.value;
    setSelectedDeliveredId(idStr);

    const p = platserLamnades.find(x => String(x.id) === idStr);

    if (!p) {
      setCaseData(prev => ({
        ...prev,
        deliveryPlace: '',
        deliveryPlaceName: '',
        deliveryPlaceGatuadress: '',
        deliveryPlaceAdress: '',
        deliveryPlacePostnummer: '',
        deliveryPlaceKommun: '',
        deliveryPlaceX: '',
        deliveryPlaceY: '',
        deliveryPlaceTyp: 'adress',
      }));
      return;
    }

    setCaseData(prev => {
      const next = {
        ...prev,
        deliveryPlaceName: p.namn || '',
        deliveryPlaceGatuadress: p.gatuadress || '',
        deliveryPlaceAdress: p.adress || '',
        deliveryPlacePostnummer: p.postnummer || '',
        deliveryPlaceKommun: p.kommun || '',
        deliveryPlaceX: p.x || '',
        deliveryPlaceY: p.y || '',
        deliveryPlaceTyp: (p.x && p.y) ? 'sweref' : 'adress',
        receiverEmail: p.email || prev.receiverEmail || '',
      };

      next.deliveryPlace = rebuildDeliveryPlaceSummary(next);
      return next;
    });
  };

  async function handleSaveDraftToServer() {
    try {
      const res = await fetch(`${API_BASE}/transport/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...caseData,
          receiverEmail: caseData.receiverEmail,
          deliveryPlace: caseData.deliveryPlace,
          contactEmail: companySettings?.contact_email || '',
          contactPerson: companySettings?.contact_person || caseData.contactPerson || '',
          contactPhone: companySettings?.kontaktTelefon || caseData.contactPhone || '',
          logo,
          previousHolders: caseData.previousHolders || [],
          companyId: currentCompany?.id,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json?.error || `Prepare HTTP ${res.status}`);
      }

      try {
        localStorage.setItem(pendingKey, JSON.stringify(caseData));
      } catch (e) {
        console.warn('[NewCase] kunde inte spara pendingTransport:', e);
      }

      alert('Utkast sparat för företaget.');
    } catch (e) {
      console.error('[NewCase] kunde inte spara utkast i databasen:', e);
      alert('Kunde inte spara utkast.\n' + e.message);
    }
  }

  function validateForNv(caseDataArg) {
    const cd = caseDataArg || caseData;
    const digits = getEwcDigits(cd.wasteCode);

    if (!digits) {
      return {
        ok: false,
        message: 'Välj en avfallskod innan du skickar till Naturvårdsverket.',
      };
    }

    if (digits.length !== 6) {
      return {
        ok: false,
        message: 'Avfallskoden måste innehålla exakt 6 siffror enligt EWC-format.',
      };
    }

    if (!cd.platsNamn || !cd.platsKommun) {
      return {
        ok: false,
        message: 'Fyll i platsnamn och kommun där avfallet senast hanterades.',
      };
    }

    return { ok: true };
  }

  async function handleSendNotesToNV() {
    try {
      const valid = validateForNv(caseData);
      if (!valid.ok) {
        alert(valid.message);
        return;
      }

      const response = await fetch(`${API_BASE}/nv/anteckning`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'anteckning',
          source: 'newcase-notes-only',
          caseData,
          companySettings: companySettings || null,
          userSettings: userSettings || null,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      alert('Anteckningar skickade till Naturvårdsverket.');
    } catch (e) {
      console.error('[NV] handleSendNotesToNV error:', e);
      alert('Kunde inte skicka till Naturvårdsverket:\n' + e.message);
    }
  }

  async function handleSendEmail(e) {
    if (e) e.preventDefault();

    const mailText = `
Transportuppdrag
---------------------
Dokumentnamn: ${caseData.name}

Transportör:
Namn: ${caseData.firstName} ${caseData.lastName}
Personnummer: ${caseData.personnummer}
E-post: ${caseData.email}
Telefon: ${caseData.phone}

Företag:
Namn: ${caseData.companyName}
Org.nr: ${caseData.orgNumber}
Kontaktperson: ${caseData.contactPerson}
Kontakt-e-post: ${companySettings?.contact_email || ""}
Kontakt-telefon: ${caseData.contactPhone}

CFAR: ${caseData.cfar}
Transportsätt: ${caseData.transportsatt}

Plats där avfallet senast hanterades:
Namn: ${caseData.platsNamn}
Gatuadress: ${caseData.platsGatuadress || ""}
Postadress: ${caseData.platsAdress}
Postnummer: ${caseData.platsPostnummer}
Kommun: ${caseData.platsKommun}
SWEREF 99 TM: ${caseData.platsX && caseData.platsY ? `${caseData.platsX}, ${caseData.platsY}` : ""}

Plats där avfallet lämnas:
${caseData.deliveryPlace || "-"}

E-post till mottagare:
${caseData.receiverEmail || "-"}

Tidigare avfallsinnehavare: ${
      Array.isArray(caseData.previousHolders) && caseData.previousHolders.length > 0
        ? caseData.previousHolders.join(", ")
        : "-"
    }

Ärende:
Avfallskod: ${caseData.wasteCode}
Typ av avfall: ${caseData.wasteTypes}
Mängd: ${caseData.amount} ${caseData.unit}
Upphämtningsadress: ${caseData.pickupAddress}
Upphämtningsdatum: ${caseData.pickupDate}
Övriga anteckningar: ${caseData.notes}
`;

    const to =
      (caseData.receiverEmail || '').trim() ||
      (companySettings?.receiver_email || '').trim() ||
      (caseData.email || '').trim();

    if (!to) {
      alert('Ingen mottagar-e-post angiven. Fyll i "E-post till mottagare av avfallet".');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/send-transport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...caseData,
          subject: 'Nytt transportärende',
          message: mailText,
          to,
          logo,
          contactEmail: companySettings?.contact_email || '',
          companyId: currentCompany?.id,
        }),
      });

      if (response.ok) {
        const prevTransKey = `transporter:${nsCompany}:${nsUser}`;
        const prevTrans = JSON.parse(localStorage.getItem(prevTransKey) || "[]");
        prevTrans.push({
          ...caseData,
          createdAt: new Date().toISOString(),
          createdBy: caseData.companyName || '',
          id: Date.now(),
        });
        localStorage.setItem(prevTransKey, JSON.stringify(prevTrans));
        alert('Ärendet har skickats till e-post!');
      } else {
        const errData = await response.json().catch(() => null);
        alert('Något gick fel: ' + (errData?.error || 'Okänt fel'));
      }
    } catch (err) {
      alert('Kunde inte skicka ärendet. Försök igen.\n' + err.message);
    }
  }

  // ========= BANKID =========
  const [showBankIdCard, setShowBankIdCard] = useState(false);
  const bankIdRegionRef = useRef(null);

  const [bankIdDocId, setBankIdDocId] = useState(null);
  const [bankIdMode, setBankIdMode] = useState(null);
  const [bankIdAppLink, setBankIdAppLink] = useState('');
  const [bankIdOrderRef, setBankIdOrderRef] = useState('');
  const [bankIdQrStartToken, setBankIdQrStartToken] = useState('');
  const [bankIdStatus, setBankIdStatus] = useState('idle');
  const [bankIdHint, setBankIdHint] = useState('');
  const [bankIdError, setBankIdError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  async function startBankIdFlow() {
    try {
      const valid = validateForNv(caseData);
      if (!valid.ok) {
        alert(valid.message);
        return;
      }

      try {
        localStorage.setItem(pendingKey, JSON.stringify(caseData));
      } catch (e) {
        console.warn('[NewCase] kunde inte spara pendingTransport:', e);
      }

      setBankIdError('');
      setBankIdHint('');
      setBankIdStatus('starting');

      const prepareRes = await fetch(`${API_BASE}/transport/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...caseData,
          receiverEmail: caseData.receiverEmail,
          deliveryPlace: caseData.deliveryPlace,
          contactEmail: companySettings?.contact_email || '',
          contactPerson: companySettings?.contact_person || caseData.contactPerson || '',
          contactPhone: companySettings?.kontaktTelefon || caseData.contactPhone || '',
          logo,
          previousHolders: caseData.previousHolders || [],
          companyId: currentCompany?.id,
        }),
      });

      if (!prepareRes.ok) {
        const err = await prepareRes.json().catch(() => ({}));
        throw new Error(err.error || `Prepare HTTP ${prepareRes.status}`);
      }

      const { id } = await prepareRes.json();
      setBankIdDocId(id);

      const startRes = await fetch(`${API_BASE}/documents/${id}/bankid/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          role: 'transporter',
          personalNumber: caseData.personnummer || undefined,
          platform: 'app',
        }),
      });

      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({}));
        throw new Error(err.error || `Start HTTP ${startRes.status}`);
      }

      const startJson = await startRes.json();

      setBankIdMode(startJson.mode || null);
      setBankIdAppLink(startJson.appLink || '');
      setBankIdOrderRef(startJson.orderRef || '');
      setBankIdQrStartToken(startJson.qrStartToken || '');
      setBankIdStatus('pending');

      if (pollRef.current) clearInterval(pollRef.current);

      pollRef.current = setInterval(async () => {
        try {
          const collectRes = await fetch(`${API_BASE}/documents/${id}/bankid/collect`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ role: 'transporter' }),
          });

          if (!collectRes.ok) {
            const err = await collectRes.json().catch(() => ({}));
            throw new Error(err.error || `Collect HTTP ${collectRes.status}`);
          }

          const col = await collectRes.json();

          if (col.status === 'pending') {
            setBankIdHint(col.hintCode || '');
          } else if (col.status === 'complete') {
            setBankIdStatus('complete');
            setBankIdHint('');
            clearInterval(pollRef.current);
            pollRef.current = null;
          } else if (col.status === 'failed') {
            setBankIdStatus('error');
            setBankIdError('BankID misslyckades. Försök igen.');
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (e) {
          setBankIdStatus('error');
          setBankIdError(e.message);
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 2000);
    } catch (e) {
      setBankIdStatus('error');
      setBankIdError(e.message);
    }
  }

  function clickBankIdButton() {
    if (bankIdStatus === 'idle') {
      return startBankIdFlow();
    }

    if (bankIdStatus === 'starting') {
      return;
    }

    if (bankIdStatus === 'pending' && bankIdAppLink) {
      try {
        window.open(bankIdAppLink, '_blank', 'noopener,noreferrer');
      } catch {
        window.location.href = bankIdAppLink;
      }
    }
  }

  async function finalizeAfterBankId() {
    if (!bankIdDocId) return;

    const to =
      (caseData.receiverEmail || '').trim() ||
      (companySettings?.receiver_email || '').trim() ||
      (caseData.email || '').trim();

    if (!to) {
      alert('Ingen mottagar-e-post angiven (receiverEmail).');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/transport/finalize/${bankIdDocId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Finalize HTTP ${res.status}`);
      }

      alert('Signerat med BankID och skickat!');

      setBankIdStatus('idle');
      setBankIdDocId(null);
      setBankIdAppLink('');
      setBankIdOrderRef('');
      setBankIdQrStartToken('');
      setBankIdHint('');
      setBankIdError('');
      setShowBankIdCard(false);
    } catch (e) {
      alert('Kunde inte slutföra och skicka: ' + e.message);
    }
  }

  const bankIdStyles = {
    cardBtn: {
      width: '100%',
      border: 0,
      padding: '18px 20px',
      borderRadius: 18,
      background: '#ECF9F0',
      boxShadow: '0 14px 38px rgba(16,185,129,0.16)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: bankIdStatus === 'starting' ? 'not-allowed' : 'pointer',
    },
    title: { fontSize: 22, lineHeight: 1.2, margin: 0, fontWeight: 700, color: '#0f172a' },
    caption: { marginTop: 8, color: '#667085', fontSize: 14 },
    rightLogoWrap: {
      background: '#EEF4F2',
      borderRadius: 16,
      width: 84,
      height: 84,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 16,
      flexShrink: 0,
    },
    rightLogo: { width: 52, height: 52, objectFit: 'contain' },
    completeWrap: { marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 },
    finishBtn: {
      marginTop: 10,
      padding: '10px 14px',
      borderRadius: 12,
      border: 0,
      background: '#16a34a',
      color: '#fff',
      fontWeight: 700,
      cursor: 'pointer',
    },
    error: {
      marginTop: 10,
      color: '#b42318',
      background: '#fee4e2',
      borderRadius: 10,
      padding: '10px 12px',
    },
  };


// ========= TEST: EGEN ELEKTRONISK SIGNERING =========
async function testAccountSigning() {
  try {
    const valid = validateForNv(caseData);

    if (!valid.ok) {
      alert(valid.message);
      return;
    }

    // 1. Skapa utkast
    const prepareRes = await fetch(`${API_BASE}/transport/prepare`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...caseData,
        receiverEmail: caseData.receiverEmail,
        deliveryPlace: caseData.deliveryPlace,
        contactEmail: companySettings?.contact_email || '',
        contactPerson:
          companySettings?.contact_person ||
          caseData.contactPerson ||
          '',
        contactPhone:
          companySettings?.kontaktTelefon ||
          caseData.contactPhone ||
          '',
        logo,
        previousHolders: caseData.previousHolders || [],
        companyId: currentCompany?.id,
      }),
    });

    const prepareJson = await prepareRes.json().catch(() => ({}));

    if (!prepareRes.ok) {
      throw new Error(
        prepareJson?.error || `Prepare HTTP ${prepareRes.status}`
      );
    }

    const documentId = prepareJson.id;

    if (!documentId) {
      throw new Error('Backend returnerade inget dokument-id');
    }

    console.log('[TEST SIGN] Utkast skapat:', documentId);

    // 2. Signera utkastet
    const signRes = await fetch(
      `${API_BASE}/documents/${documentId}/sign`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      }
    );

    const signJson = await signRes.json().catch(() => ({}));

    if (!signRes.ok) {
      throw new Error(
        signJson?.error || `Sign HTTP ${signRes.status}`
      );
    }

    console.log('[TEST SIGN] Signering klar:', signJson);


    // 3. SÄKERHETSTEST:
// Försök ändra dokumentet EFTER att det signerats.
// Backend SKA blockera detta med HTTP 409.
const tamperRes = await fetch(
  `${API_BASE}/documents/${documentId}`,
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      payload: {
        name: 'MANIPULERAT TEST',
      },
    }),
  }
);

const tamperJson = await tamperRes.json().catch(() => ({}));

console.log('[TEST SIGN] Manipulationstest:', {
  status: tamperRes.status,
  response: tamperJson,
});

if (
  tamperRes.status !== 409 ||
  tamperJson?.code !== 'DOCUMENT_ALREADY_SIGNED'
) {
  throw new Error(
    `SÄKERHETSTEST MISSLYCKADES! ` +
    `Backend tillät eller hanterade ändringen oväntat. ` +
    `HTTP ${tamperRes.status}`
  );
}


    alert(
  `SÄKERHETSTEST GODKÄNT! 🔒\n\n` +
  `Dokument-ID: ${documentId}\n` +
  `Signerad av: ${signJson?.signature?.name || '-'}\n` +
  `Tid: ${signJson?.signature?.signed_at || '-'}\n\n` +
  `✅ Elektronisk signering sparades\n` +
  `✅ Ändring efter signering blockerades (HTTP 409)\n\n` +
  `Dokumentet har INTE skickats till Naturvårdsverket.`
);
  } catch (e) {
    console.error('[TEST SIGN] Fel:', e);

    alert(
      'Testsigneringen misslyckades:\n' +
      (e?.message || 'Okänt fel')
    );
  }
}


  async function handleSendToNV() {
    setShowBankIdCard(true);

    requestAnimationFrame(() => {
      if (bankIdRegionRef.current) {
        bankIdRegionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    });

    if (bankIdStatus === 'idle') {
      await startBankIdFlow();
    }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function addPrevHolderEmail(val) {
    const e = (val || '').trim().toLowerCase();
    if (!e) return;
    if (!emailRegex.test(e)) return alert('Ange en giltig e-postadress.');
    if ((caseData.previousHolders || []).includes(e)) return;

    setCaseData(prev => ({
      ...prev,
      previousHolders: [...(prev.previousHolders || []), e],
    }));
  }

  function removePrevHolderEmail(val) {
    setCaseData(prev => ({
      ...prev,
      previousHolders: (prev.previousHolders || []).filter(x => x !== val),
    }));
  }

  const grundPrev = Array.isArray(companySettings?.previous_holders_list)
    ? companySettings.previous_holders_list
    : [];

  if (!identityReady || companySettingsStatus === 'loading') {
    return (
      <div className="newcase-container">
        <div style={{ marginBottom: "1.2rem" }}>
          <BackToDashboard label="← Tillbaka till Dashboard" />
        </div>
        <p>Laddar uppgifter…</p>
      </div>
    );
  }

  return (
    <div className="newcase-container">
      <div style={{ marginBottom: "1.2rem" }}>
        <BackToDashboard label="← Tillbaka till Dashboard" />
      </div>

      <h2 className="newcase-title">Starta transport</h2>

      {logo && (
        <div style={{ marginBottom: 24, marginTop: 0, display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={logo}
            alt="Logga"
            style={{ width: 48, height: 48, borderRadius: 8, objectFit: "contain", background: "#f2f2f2", border: "1px solid #ddd" }}
          />
          <span style={{ color: "#555" }}>Företagslogga</span>
        </div>
      )}

      <section className="selected-codes">
        <h3>Valda avfallskoder</h3>
        {savedCodes.length > 0 ? (
          <ul className="selected-list">
            {savedCodes.map((item, idx) => {
              const fullCode = buildFullEwcCode(item);
              const isActive = selectedCodeIdx === idx;
              const justSel = lastJustSelected === fullCode;
              const isHazardous = isHazardousWasteItem(item);

              return (
                <li
                  key={idx}
                  className={`selected-item${isActive ? ' is-active' : ''}${justSel ? ' just-selected' : ''}`}
                  onClick={() => handleSelectCode(item, idx)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectCode(item, idx);
                    }
                  }}
                  aria-pressed={isActive}
                >
                  <div className="selected-text">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span className="selected-code">{fullCode}</span>
                      {isHazardous && (
                        <span className="hazard-chip">Farligt avfall</span>
                      )}
                    </div>
                    <span className="selected-desc">{item.description}</span>
                  </div>

                  <button
                    type="button"
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCode(item.code, idx);
                    }}
                    title="Ta bort kod"
                    aria-label={`Ta bort kod ${fullCode}`}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="no-codes">Inga valda koder</p>
        )}
      </section>

      <form onSubmit={handleSendEmail} className="newcase-form">
        <div className="form-group">
          <label htmlFor="name"><b>Namnge dokumentet (valfritt)</b></label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Ex: 'Farligt avfall juni 2025'"
            value={caseData.name}
            onChange={handleChange}
            maxLength={60}
            autoComplete="off"
            style={{ fontWeight: 500, background: "#f7f9f6" }}
          />
        </div>

        <fieldset>
          <legend>Transportör (Uppgiftslämnare)</legend>
          <div className="form-group"><label>Personnummer</label><input type="text" name="personnummer" value={caseData.personnummer} readOnly /></div>
          <div className="form-group"><label>Förnamn</label><input type="text" name="firstName" value={caseData.firstName} readOnly /></div>
          <div className="form-group"><label>Efternamn</label><input type="text" name="lastName" value={caseData.lastName} readOnly /></div>
          <div className="form-group"><label>E-post</label><input type="email" name="email" value={caseData.email} readOnly /></div>
          <div className="form-group"><label>Telefon</label><input type="tel" name="phone" value={caseData.phone} readOnly /></div>
        </fieldset>

        <fieldset>
          <legend>Verksamhetsutövare (Företag)</legend>
          <div className="form-group"><label>Organisationsnummer</label><input type="text" name="orgNumber" value={caseData.orgNumber} readOnly /></div>
          <div className="form-group"><label>Verksamhetens namn</label><input type="text" name="companyName" value={caseData.companyName} readOnly /></div>
          <div className="form-group"><label>Kontaktperson</label><input type="text" name="contactPerson" value={caseData.contactPerson} readOnly /></div>
          <div className="form-group"><label>E-post (kontaktperson)</label><input type="email" name="contactEmail" value={companySettings?.contact_email || ''} readOnly /></div>
          <div className="form-group"><label>Telefon (kontaktperson)</label><input type="tel" name="contactPhone" value={caseData.contactPhone} readOnly /></div>
        </fieldset>

        <fieldset>
          <legend>Tidigare avfallsinnehavare</legend>

          {Array.isArray(grundPrev) && grundPrev.length > 0 && (
            <div className="form-group">
              <label>Välj från Grunduppgifter (företag)</label>
              <select
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) addPrevHolderEmail(v);
                  e.target.value = '';
                }}
                defaultValue=""
              >
                <option value="">-- Välj e-post --</option>
                {grundPrev.map((em) => (
                  <option key={em} value={em}>{em}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Lägg till ny e-post</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                placeholder="namn@exempel.se"
                value={prevHolderInput}
                onChange={(e) => setPrevHolderInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPrevHolderEmail(prevHolderInput);
                    setPrevHolderInput('');
                  }
                }}
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  addPrevHolderEmail(prevHolderInput);
                  setPrevHolderInput('');
                }}
              >
                Lägg till
              </button>
            </div>
          </div>

          {Array.isArray(caseData.previousHolders) && caseData.previousHolders.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {caseData.previousHolders.map((em) => (
                <span
                  key={em}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 999,
                    border: '1px solid #cde7db',
                    background: '#eaf7ef',
                    color: '#166534',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {em}
                  <button
                    type="button"
                    onClick={() => removePrevHolderEmail(em)}
                    title="Ta bort"
                    style={{ border: 'none', background: 'transparent', color: '#0f5132', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </fieldset>

        <fieldset>
          <legend>Plats där avfallet senast hanterades</legend>

          {platser.length > 0 && (
            <div className="form-group">
              <label>Välj plats</label>
              <select
                onChange={handlePlatsSelect}
                value={
                  platser.find(p =>
                    p.namn === caseData.platsNamn &&
                    String(p.gatuadress || '') === String(caseData.platsGatuadress || '') &&
                    p.adress === caseData.platsAdress &&
                    p.postnummer === caseData.platsPostnummer &&
                    p.kommun === caseData.platsKommun &&
                    String(p.x || '') === String(caseData.platsX || '') &&
                    String(p.y || '') === String(caseData.platsY || '')
                  )?.id || ''
                }
              >
                <option value="">-- Välj plats --</option>
                {platser.map(plats => (
                  <option key={plats.id} value={plats.id}>
                    {plats.namn}
                    {plats.gatuadress ? ` – ${plats.gatuadress}` : ''}
                    {plats.adress ? (plats.gatuadress ? `, ${plats.adress}` : ` – ${plats.adress}`) : ''}
                    {(plats.postnummer || plats.kommun) ? `, ${plats.postnummer ?? ''} ${plats.kommun ?? ''}` : ''}
                    {(!plats.adress && !plats.gatuadress && plats.x && plats.y) ? ` – SWEREF: ${plats.x}, ${plats.y}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 10 }}>
            <label style={{ fontWeight: 600 }}>Välj plats på karta</label>
            <MapPicker
              onChangeXY={({ x, y }) => {
                setCaseData(prev => ({ ...prev, platsX: String(x), platsY: String(y) }));
              }}
              onChangeLatLng={() => {}}
              onAddressResolved={({ postnummer, kommun, gatuadress, postadress }) => {
                setCaseData(prev => ({
                  ...prev,
                  platsGatuadress: gatuadress || prev.platsGatuadress,
                  platsAdress: postadress || prev.platsAdress,
                  platsPostnummer: postnummer || prev.platsPostnummer,
                  platsKommun: kommun || prev.platsKommun,
                }));
              }}
            />
          </div>

          <div className="form-group"><label>Namn</label><input type="text" name="platsNamn" value={caseData.platsNamn} onChange={handleChange} required /></div>
          <div className="form-group"><label>Gatuadress</label><input type="text" name="platsGatuadress" value={caseData.platsGatuadress} onChange={handleChange} /></div>
          <div className="form-group"><label>Postadress</label><input type="text" name="platsAdress" value={caseData.platsAdress} onChange={handleChange} /></div>
          <div className="form-group"><label>Postnummer</label><input type="text" name="platsPostnummer" value={caseData.platsPostnummer} onChange={handleChange} /></div>

          <div className="form-group" style={{ position: 'relative', overflow: 'visible' }}>
            <label>Kommun</label>
            <input
              type="text"
              name="platsKommun"
              placeholder="Sök kommun..."
              value={caseData.platsKommun}
              onChange={(e) => { handleChange(e); setShowKommuner(true); }}
              onFocus={() => setShowKommuner(true)}
              onBlur={() => setTimeout(() => setShowKommuner(false), 150)}
              autoComplete="off"
            />
            {(() => {
              const q = (caseData.platsKommun || '').trim().toLowerCase();
              const starts = q ? SAFE_KOMMUNER.filter(k => k.toLowerCase().startsWith(q)) : [];
              const contains = q ? SAFE_KOMMUNER.filter(k => !k.toLowerCase().startsWith(q) && k.toLowerCase().includes(q)) : [];
              const kommunMatches = [...starts, ...contains].slice(0, 8);

              return (showKommuner && q && kommunMatches.length > 0) ? (
                <ul
                  role="listbox"
                  style={{
                    position: 'absolute',
                    zIndex: 9999,
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    margin: 0,
                    padding: '6px 0',
                    listStyle: 'none',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                    maxHeight: 240,
                    overflowY: 'auto',
                  }}
                >
                  {kommunMatches.map((k) => (
                    <li
                      key={k}
                      role="option"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setCaseData(prev => ({ ...prev, platsKommun: k }));
                        setShowKommuner(false);
                      }}
                      style={{ padding: '8px 12px', cursor: 'pointer' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {k}
                    </li>
                  ))}
                </ul>
              ) : null;
            })()}
          </div>

          <div className="form-group"><label>X-koordinat (SWEREF 99 TM)</label><input type="text" name="platsX" value={caseData.platsX} onChange={handleChange} /></div>
          <div className="form-group"><label>Y-koordinat (SWEREF 99 TM)</label><input type="text" name="platsY" value={caseData.platsY} onChange={handleChange} /></div>
        </fieldset>

        <fieldset>
          <legend>Transport</legend>
          <div className="form-group">
            <label>Transportsätt</label>
            <input
              type="text"
              name="transportsatt"
              value={caseData.transportsatt}
              readOnly
            />
          </div>
        </fieldset>

        <fieldset>
          <legend>Plats där avfallet lämnas</legend>

          {platserLamnades.length > 0 && (
            <div className="form-group">
              <label>Välj sparad plats</label>
              <select
                value={selectedDeliveredId}
                onChange={handleDeliveredPlaceSelect}
              >
                <option value="">-- Välj plats --</option>
                {platserLamnades.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.namn}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Typ av plats</label>
            <select
              name="deliveryPlaceTyp"
              value={caseData.deliveryPlaceTyp || "adress"}
              onChange={handleChange}
            >
              <option value="adress">Adress</option>
              <option value="sweref">SWEREF 99 TM</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="deliveryPlaceName">Namn (valfritt)</label>
            <input
              type="text"
              id="deliveryPlaceName"
              name="deliveryPlaceName"
              placeholder="Ex: ÅVC Tagene"
              value={caseData.deliveryPlaceName || ""}
              onChange={handleChange}
            />
          </div>

          {caseData.deliveryPlaceTyp !== "sweref" ? (
            <>
              <div className="form-group">
                <label htmlFor="deliveryPlaceGatuadress">Gatuadress</label>
                <input
                  type="text"
                  id="deliveryPlaceGatuadress"
                  name="deliveryPlaceGatuadress"
                  placeholder="Ex: Tagenevägen 12"
                  value={caseData.deliveryPlaceGatuadress || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="deliveryPlaceAdress">Postadress</label>
                <input
                  type="text"
                  id="deliveryPlaceAdress"
                  name="deliveryPlaceAdress"
                  placeholder="Ex: 424 39 Göteborg"
                  value={caseData.deliveryPlaceAdress || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="deliveryPlacePostnummer">Postnummer</label>
                <input
                  type="text"
                  id="deliveryPlacePostnummer"
                  name="deliveryPlacePostnummer"
                  placeholder="Ex: 42439"
                  value={caseData.deliveryPlacePostnummer || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="deliveryPlaceKommun">Kommun</label>
                <input
                  type="text"
                  id="deliveryPlaceKommun"
                  name="deliveryPlaceKommun"
                  placeholder="Ex: Göteborg"
                  value={caseData.deliveryPlaceKommun || ""}
                  onChange={handleChange}
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="deliveryPlaceX">X-koordinat (SWEREF 99 TM)</label>
                <input
                  type="text"
                  id="deliveryPlaceX"
                  name="deliveryPlaceX"
                  placeholder="Ex: 319000"
                  value={caseData.deliveryPlaceX || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="deliveryPlaceY">Y-koordinat (SWEREF 99 TM)</label>
                <input
                  type="text"
                  id="deliveryPlaceY"
                  name="deliveryPlaceY"
                  placeholder="Ex: 6400000"
                  value={caseData.deliveryPlaceY || ""}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="deliveryPlaceSummary">Plats där avfallet lämnas (sammanfattning)</label>
            <input
              type="text"
              id="deliveryPlaceSummary"
              name="deliveryPlace"
              placeholder="Byggs automatiskt (men kan redigeras om du vill)"
              value={caseData.deliveryPlace || ""}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="receiverEmail">E-post till mottagare av avfallet</label>
            <input
              type="email"
              id="receiverEmail"
              name="receiverEmail"
              placeholder="mottagare@foretag.se"
              value={caseData.receiverEmail || ""}
              onChange={handleChange}
            />
          </div>
        </fieldset>

        <fieldset>
          <legend>Transportärende</legend>

          <div className="form-group">
            <label htmlFor="wasteCode">Avfallskod</label>
            <input
              type="text"
              id="wasteCode"
              name="wasteCode"
              placeholder="Klicka på en vald kod ovan"
              value={caseData.wasteCode}
              readOnly
            />
            <button
              type="button"
              className="btn-secondary"
              style={{ marginTop: 8 }}
              onClick={handleSaveCurrentCode}
            >
              Spara denna kod för företaget
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="wasteTypes">Typ av avfall</label>
            <input
              type="text"
              id="wasteTypes"
              name="wasteTypes"
              placeholder="Ex. Batterier, Kemikalier"
              value={caseData.wasteTypes}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="amount">Mängd</label>
            <input
              type="number"
              id="amount"
              name="amount"
              placeholder="Antal (t.ex. 10)"
              value={caseData.amount}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit">Enhet</label>
            <select
              id="unit"
              name="unit"
              value={caseData.unit}
              onChange={handleChange}
              required
            >
              <option value="kg">kg</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="pickupDate">Upphämtningsdatum</label>
            <input
              type="date"
              id="pickupDate"
              name="pickupDate"
              value={caseData.pickupDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Övriga anteckningar</label>
            <textarea
              id="notes"
              name="notes"
              rows="3"
              placeholder="T.ex. säkerhetsinformation"
              value={caseData.notes}
              onChange={handleChange}
            />
          </div>
        </fieldset>

        <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
          <button
            type="button"
            onClick={handleSaveDraftToServer}
            style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 12,
              padding: '14px 16px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <div style={{ fontWeight: 600, color: '#065f46' }}>
              Spara anteckningar / Transportdokument
            </div>

            <div
              style={{
                fontSize: '0.85rem',
                color: '#16a34a',
                fontWeight: 400,
                lineHeight: 1.4,
              }}
            >
              Du kan spara anteckningar och transportdokument och sedan skicka till
              Naturvårdsverket från "Sparade och skickade Transportdokument"
            </div>
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSendToNV}
            >
              Spara och signera Transportdokument
            </button>
<button
  type="button"
  onClick={testAccountSigning}
  style={{
    marginTop: 10,
    padding: '12px 16px',
    borderRadius: 10,
    border: '2px dashed #64748b',
    background: '#f8fafc',
    color: '#334155',
    fontWeight: 700,
    cursor: 'pointer',
    width: '100%',
  }}
>
  🧪 Testa egen signering
</button>

            <div
              style={{
                fontSize: '0.85rem',
                color: '#16a34a',
                fontWeight: 400,
                lineHeight: 1.4,
                paddingLeft: 4,
              }}
            >
              Du kan spara ett transportdokument och sedan skicka till Naturvårdsverket
              från "Sparade och skickade Transportdokument"
            </div>
          </div>

          <button type="button" className="btn-primary" onClick={handleSendNotesToNV}>
            Skicka anteckningar till Naturvårdsverket
          </button>
        </div>

        {showBankIdCard && (
          <div ref={bankIdRegionRef} role="region" aria-label="BankID signering" style={{ marginTop: 18 }}>
            <button
              type="button"
              onClick={clickBankIdButton}
              disabled={bankIdStatus === 'starting'}
              style={bankIdStyles.cardBtn}
              aria-busy={bankIdStatus === 'starting'}
              aria-label="Signera med Mobilt BankID"
            >
              <div style={{ flex: 1, paddingRight: 12 }}>
                <h4 style={bankIdStyles.title}>
                  Signera med Mobilt BankID
                </h4>
                <div style={bankIdStyles.caption}>
                  {bankIdStatus === 'starting'
                    ? 'Skapar utkast…'
                    : bankIdStatus === 'pending'
                      ? 'Tryck för att öppna BankID-appen'
                      : 'Tryck för att påbörja signering'}
                </div>
              </div>

              <div style={bankIdStyles.rightLogoWrap} aria-hidden>
                <img src="/img/BankID_logo.png" alt="BankID" style={bankIdStyles.rightLogo} />
              </div>
            </button>

            {bankIdStatus === 'error' && (
              <div style={bankIdStyles.error}>{bankIdError}</div>
            )}

            {bankIdStatus === 'complete' && (
              <>
                <div style={bankIdStyles.completeWrap}>
                  <div style={{ fontSize: 20, color: '#16a34a' }}>✔</div>
                  <div>BankID-signering klar.</div>
                </div>
                <button type="button" onClick={finalizeAfterBankId} style={bankIdStyles.finishBtn}>
                  Slutför & skicka dokument
                </button>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

export default NewCasePage;